import i18next from 'i18next';
import produce from 'immer';
import {
	IStepExportFormInfo,
	stepExportInitialValues,
} from '../components/registerShipment/registerShipmentForm/stepExport/stepExport.schema';
import {
	IStepInfoFormInfo,
	stepInfoInitialValues,
} from '../components/registerShipment/registerShipmentForm/stepInfo/stepInfo.schema';
import {
	IStepOptionalServicesFormInfo,
	stepOptionalServicesInitialValues,
} from '../components/registerShipment/registerShipmentForm/stepOptionalServices/stepOptionalServices.schema';
import {
	IStepShipmentFormInfo,
	stepShipmentInitialValues,
} from '../components/registerShipment/registerShipmentForm/stepShipment/stepShipment.schema';
import {
	IStepTypeFormInfo,
	stepTypeInitialValues,
} from '../components/registerShipment/registerShipmentForm/stepType/stepType.schema';
import {
	RESET_SUBMISSION_FAILURE,
	RESET_TO_INITIAL_VALUES,
	SET_COD_OPTIONAL_SERVICE_PRICE_FULFILLED,
	SET_INSURANCE_OPTIONAL_SERVICE_PRICE_FULFILLED,
	STEP_EXPORT_FULFILLED,
	STEP_INFO_FULFILLED,
	STEP_OPTIONAL_SERVICES_FULFILLED,
	STEP_SHIPMENT_FULFILLED,
	STEP_SHIPMENT_SENDER_SSN_ADDED,
	STEP_TYPE_COUNTRY_ADDED,
	STEP_TYPE_FULFILLED,
	SUBMIT_DOMESTIC_SHIPMENT_FAILED,
	SUBMIT_DOMESTIC_SHIPMENT_FULFILLED,
	SUBMIT_DOMESTIC_SHIPMENT_PENDING,
	SUBMIT_INTERNATIONAL_SHIPMENT_FAILED,
	SUBMIT_INTERNATIONAL_SHIPMENT_FULFILLED,
	SUBMIT_INTERNATIONAL_SHIPMENT_PENDING,
	UPDATE_TOTAL_PRICE,
} from '../constants/actions/registerShipmentFormActions';
import { LOGOUT_FULFILLED } from '../constants/actions/userProfileActions';
import { IDomesticShipmentReturnDataModel } from '../interfaces/apiModels/domesticShipmentDataModel';
import { REGISTER_SHIPMENT_SET_INITIALVALUES } from './../constants/actions/registerShipmentFormActions';

export interface IRegisterShipmentReducer {
	stepTypeFormInfo: IStepTypeFormInfo;
	stepShipmentFormInfo: IStepShipmentFormInfo;
	stepOptionalServicesFormInfo: IStepOptionalServicesFormInfo;
	stepInfoFormInfo: IStepInfoFormInfo;
	stepExportFormInfo: IStepExportFormInfo;
	readOnlyValuesMapper: {
		[key: string]: boolean;
	};
	submissionLoading: boolean;
	submissionFulfilled: boolean;
	submissionFailed: boolean;
	customerId: string;
	registrationKey: string;
	shipmentId: string;
	insuranceOptionalServiceCustomPrice: number;
	codOptionalServiceCustomPrice: number;
	receiverAddressTemp: string;
	totalPrice: number;
	submissionErrorMessage: string;
	initializedSender: boolean;
}

const initialValues: IRegisterShipmentReducer = {
	stepTypeFormInfo: stepTypeInitialValues,
	stepShipmentFormInfo: stepShipmentInitialValues,
	stepOptionalServicesFormInfo: stepOptionalServicesInitialValues,
	stepInfoFormInfo: stepInfoInitialValues,
	stepExportFormInfo: stepExportInitialValues,
	readOnlyValuesMapper: {
		receiverPostCode: true,
	},
	submissionLoading: false,
	submissionFulfilled: false,
	submissionFailed: false,
	submissionErrorMessage: '',
	customerId: '',
	registrationKey: '',
	shipmentId: '',
	insuranceOptionalServiceCustomPrice: 0,
	codOptionalServiceCustomPrice: 0,
	receiverAddressTemp: '',
	totalPrice: 0,
	initializedSender: false,
};

const formatSubmissionError = (error: unknown): string => {
	if (error == null) return '';
	if (typeof error === 'string') return error;
	if (typeof error === 'object') {
		const message = (error as { message?: string }).message;
		if (message && typeof message === 'string') return message;
		try {
			return JSON.stringify(error);
		} catch {
			return '';
		}
	}
	return String(error);
};

const resolveSubmissionErrorMessage = (state: IRegisterShipmentReducer, error: unknown): string => {
	const formatted = formatSubmissionError(error);
	if (state.stepInfoFormInfo?.country?.value === 'US' && error === 'invalid_regtype - Can not create Shipment ') {
		return i18next.t('myShipments:invalidRegTypeUS');
	}
	return formatted;
};

const registerShipmentReducer = produce((draft: IRegisterShipmentReducer, action) => {
	switch (action.type) {
		case RESET_SUBMISSION_FAILURE: {
			draft.submissionFailed = false;
			break;
		}
		case REGISTER_SHIPMENT_SET_INITIALVALUES: {
			draft.initializedSender = true;
			break;
		}
		case STEP_TYPE_COUNTRY_ADDED: {
			draft.stepInfoFormInfo.country = action.payload.country;
			break;
		}
		case STEP_TYPE_FULFILLED: {
			const payloadValues: IStepTypeFormInfo = action.payload.values;
			draft.stepTypeFormInfo = payloadValues;

			// If the user goes back and changes the country, we have to reset
			// his choice of optional services
			draft.stepOptionalServicesFormInfo.optionalServices = [];

			// If the user goes back and changes the country, we have to reset
			// his choice of service
			draft.stepShipmentFormInfo = stepShipmentInitialValues;
			break;
		}
		case STEP_SHIPMENT_FULFILLED: {
			const payloadValues: IStepShipmentFormInfo = action.payload.values;
			draft.stepShipmentFormInfo = payloadValues;

			if (payloadValues.serviceOption === 'DPO' || payloadValues.serviceOption === 'DPT') {
				// The receiver address is automatically set when shipping to a postbox
				payloadValues.serviceOption === 'DPO'
					? (draft.stepInfoFormInfo.receiverAddress = payloadValues.serviceOptionPostbox.label)
					: (draft.stepInfoFormInfo.receiverAddress = payloadValues.serviceOptionParcelpoint.label);

				// Setting the value as read only
				draft.readOnlyValuesMapper.receiverAddress = true;
			} else {
				draft.readOnlyValuesMapper.receiverAddress = false;
				// If the user goes back and changes the shipment type we have to reset
				// his address and post code in case it had been filled out by the postbox option
				draft.stepInfoFormInfo.receiverAddress = draft.receiverAddressTemp;
				// draft.stepInfoFormInfo.receiverPostCode = draft.stepTypeFormInfo.toPostalCode;
			}

			// If the user goes back and changes the shipment type, we have to reset
			// his choice of optional services
			draft.stepOptionalServicesFormInfo = stepOptionalServicesInitialValues;

			break;
		}
		case STEP_OPTIONAL_SERVICES_FULFILLED: {
			draft.stepOptionalServicesFormInfo = action.payload.values;
			break;
		}
		case STEP_SHIPMENT_SENDER_SSN_ADDED: {
			draft.stepInfoFormInfo.senderSsn = action.payload.values;
			break;
		}
		case STEP_INFO_FULFILLED: {
			draft.stepInfoFormInfo = action.payload.values;
			// Keep address if user switches to postbox and then to another shipment type
			if (draft.stepShipmentFormInfo.serviceOption !== 'DPO') {
				draft.receiverAddressTemp = draft.stepInfoFormInfo.receiverAddress;
			}
			if (draft.stepShipmentFormInfo.serviceOption !== 'DPT') {
				draft.receiverAddressTemp = draft.stepInfoFormInfo.receiverAddress;
			}
			break;
		}
		case STEP_EXPORT_FULFILLED: {
			draft.stepExportFormInfo = action.payload.values;
			break;
		}
		case SUBMIT_DOMESTIC_SHIPMENT_PENDING: {
			draft.submissionLoading = true;
			draft.submissionFailed = false;
			break;
		}
		case SUBMIT_DOMESTIC_SHIPMENT_FULFILLED: {
			const returnData: IDomesticShipmentReturnDataModel = action.payload.data;
			draft.submissionFulfilled = true;
			draft.registrationKey = returnData.registrationKey;
			draft.shipmentId = returnData.shipmentId;
			draft.customerId = returnData.customerId;

			break;
		}
		case SUBMIT_DOMESTIC_SHIPMENT_FAILED: {
			draft.submissionLoading = false;
			draft.submissionFailed = true;
			draft.submissionErrorMessage = resolveSubmissionErrorMessage(draft, action.payload.error);
			break;
		}
		case SUBMIT_INTERNATIONAL_SHIPMENT_PENDING: {
			draft.submissionLoading = true;
			draft.submissionFailed = false;
			break;
		}
		case SUBMIT_INTERNATIONAL_SHIPMENT_FULFILLED: {
			const returnData = action.payload.data;
			draft.submissionFulfilled = true;
			draft.registrationKey = returnData.registrationKey;
			draft.shipmentId = returnData.shipmentId;
			draft.customerId = returnData.customerId;

			break;
		}
		case SUBMIT_INTERNATIONAL_SHIPMENT_FAILED: {
			draft.submissionLoading = false;
			draft.submissionFailed = true;
			draft.submissionErrorMessage = resolveSubmissionErrorMessage(draft, action.payload.error);
			break;
		}
		case SET_INSURANCE_OPTIONAL_SERVICE_PRICE_FULFILLED: {
			draft.insuranceOptionalServiceCustomPrice = action.payload.price;
			break;
		}
		case SET_COD_OPTIONAL_SERVICE_PRICE_FULFILLED: {
			draft.codOptionalServiceCustomPrice = action.payload.price;
			break;
		}
		case UPDATE_TOTAL_PRICE: {
			draft.totalPrice = action.payload.price;
			break;
		}
		case RESET_TO_INITIAL_VALUES: {
			return initialValues;
		}
		case LOGOUT_FULFILLED: {
			return initialValues;
		}
		default: {
			break;
		}
	}
}, initialValues);

export default registerShipmentReducer;
